import { History } from 'history';
import React from 'react';
import { StaticContext } from 'react-router';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import LocContext from 'sharedreact/contexts/LocContext';
import LocationState from 'sharedreact/utils/LocationState';
import UrlQuery from 'sharedreact/utils/UrlQuery';

type ActualToType = {
  pathname: string,
  search: string,
  hash?: string
  state?: LocationState
};

type ToObjectType = Omit<ActualToType, 'search'> &  { search?: string };

function toToObject (to: string | ToObjectType | null): ActualToType {
  let toObject: ActualToType = { pathname: '', search: '' };

  if (typeof to === 'string') {
    const toParts: string[] = to.split('?');
    const pathname: string = toParts[0];
    let search: string = '';
    let hash: string | null = null;
    if (toParts.length > 1) {
      const searchParts: string[] = toParts[1].split('#');
      search = searchParts[0];
      if (searchParts.length > 1) {
        hash = searchParts[1];
      }
    }

    if (hash != null) {
      toObject = {pathname: pathname, search: search, hash: hash};
    } else {
      toObject = {pathname: pathname, search: search};
    }
  } else {
    toObject = Object.assign({ pathname: '', search: '' }, to);
  }

  return toObject;
}

function _appendContext (to: string | ToObjectType | null, locContext: React.ContextType<typeof LocContext>): ActualToType {
  let toObject: ActualToType = toToObject(to);

  if (locContext != null) {
    const search: {[key: string]: string} = UrlQuery.parse(toObject.search);

    let newSearch: {[key: string]: string} = Object.assign({}, search);
    for (const [key, value] of Object.entries(locContext)) {
      // only add from context if it was not part of the search query already,
      // this enables overriding the context settings
      if (!search.hasOwnProperty(key)) {
        newSearch[key] = value;
      }
    }

    const newSearchString: string = UrlQuery.stringify(newSearch);

    if (newSearchString === '?') {
      toObject.search = '';
    } else {
      toObject.search = newSearchString;
    }
  }

  return toObject;
}

function _goToLink (toObj: ActualToType, history: History): void {
  if (history.location.pathname === toObj.pathname &&
    history.location.search === toObj.search &&
    history.location.hash === (toObj.hash || '')) {
    if (history.location.state !== toObj.state) {
      history.replace(toObj);
    } // else do nothing
  } else {
    history.push(toObj);
  }
}

type Props = RouteComponentProps<{}, StaticContext, LocationState> & {
  id: string,
  to: string | ToObjectType,
  title?: string,
  className?: string,
  children?: React.ReactNode | Iterable<React.ReactNode>,
  type?: string,

  onClick?: (goToLink: (updatedTo?: string | ToObjectType) => void) => boolean 
};

class LinkWithContext extends React.Component<Props> {
  static contextType = LocContext;
  context!: React.ContextType<typeof LocContext>

  handleClick (event:React.MouseEvent<HTMLAnchorElement, MouseEvent>, toObj: ActualToType) {
    event.stopPropagation();
    event.preventDefault();

    let needToGoToLink: boolean = true;

    const history = this.props.history;

    if (this.props.onClick != null) {
      const onClickResult = this.props.onClick((updatedTo?: string | ToObjectType) => _goToLink(updatedTo ? toToObject(updatedTo) : toObj, history));
      if (onClickResult === false) {
        needToGoToLink = false;
      }
    }

    if (needToGoToLink) {
      _goToLink(toObj, history);
    }

    return false;
  }

  override render (): React.ReactNode {
    const { id, to, title, className, type, children } = this.props;

    const toObj: ActualToType = _appendContext(to, this.context);

    const href = this.props.history.createHref(toObj);

    return (
      <a id={id}
        className={className}
        type={type}
        href={href}
        onClick={(event) => { return this.handleClick(event, toObj); }}
        title={title}
      >
        {children}
      </a>
    );
  }
}

export { ToObjectType };

export default withRouter(LinkWithContext);
